// third-party
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// project imports
import axios2 from 'utils/axios2';
import { hasPermission, getUserContext, createAuditEntry } from '../../utils/rbac';

// ----------------------------------------------------------------------

// Async thunks
export const fetchDatabaseOverview = createAsyncThunk('datahubDatabase/fetchOverview', async (projectId, { rejectWithValue }) => {
    try {
        const { user_token, user_permissions, user_groups, user_id } = getUserContext();

        // Check database admin permission for overview
        if (!hasPermission(user_permissions, 'database_admin', 'view_stats')) {
            throw new Error('Insufficient permissions: Cannot view database overview');
        }

        const response = await axios2.get('/datahub/database/overview', {
            params: {
                projectId,
                user_token,
                permissions: user_permissions,
                groups: user_groups
            }
        });

        // Create audit entry
        const auditEntry = createAuditEntry(user_id, 'fetch_database_overview', 'database', projectId, true);

        return response.data;
    } catch (error) {
        // Create audit entry for failed operation
        const { user_id } = getUserContext();
        const auditEntry = createAuditEntry(user_id, 'fetch_database_overview', 'database', projectId, false, error.message);

        return rejectWithValue(error.response?.data || error.message);
    }
});

export const queryDocuments = createAsyncThunk(
    'datahubDatabase/queryDocuments',
    async ({ projectId, collection, query = {} }, { rejectWithValue }) => {
        try {
            const { user_token, user_permissions, user_groups, user_id } = getUserContext();

            // Check query permission
            if (!hasPermission(user_permissions, 'documents', 'query')) {
                throw new Error('Insufficient permissions: Cannot query documents');
            }

            const response = await axios2.post('/datahub/database/query', {
                projectId,
                collection,
                query,
                user_token,
                permissions: user_permissions,
                groups: user_groups,
                audit: {
                    action: 'query_documents',
                    resource_type: 'documents',
                    details: {
                        collection: collection,
                        query: query
                    }
                }
            });

            // Create audit entry
            const auditEntry = createAuditEntry(user_id, 'query_documents', 'documents', collection, true);

            return response.data;
        } catch (error) {
            // Create audit entry for failed operation
            const { user_id } = getUserContext();
            const auditEntry = createAuditEntry(user_id, 'query_documents', 'documents', collection, false, error.message);

            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const insertDocument = createAsyncThunk(
    'datahubDatabase/insertDocument',
    async ({ projectId, collection, document }, { rejectWithValue }) => {
        try {
            const { user_token, user_permissions, user_groups, user_id } = getUserContext();

            // Check insert permission
            if (!hasPermission(user_permissions, 'documents', 'insert')) {
                throw new Error('Insufficient permissions: Cannot insert documents');
            }

            const response = await axios2.post('/datahub/database/insert', {
                projectId,
                collection,
                document,
                user_token,
                permissions: user_permissions,
                groups: user_groups,
                audit: {
                    action: 'insert_document',
                    resource_type: 'documents',
                    details: {
                        collection: collection,
                        document_keys: Object.keys(document)
                    }
                }
            });

            // Create audit entry
            const auditEntry = createAuditEntry(user_id, 'insert_document', 'documents', response.data.id, true);

            return response.data;
        } catch (error) {
            // Create audit entry for failed operation
            const { user_id } = getUserContext();
            const auditEntry = createAuditEntry(user_id, 'insert_document', 'documents', 'new', false, error.message);

            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const updateDocument = createAsyncThunk(
    'datahubDatabase/updateDocument',
    async ({ projectId, collection, documentId, updates }, { rejectWithValue }) => {
        try {
            const { user_token, user_permissions, user_groups, user_id } = getUserContext();

            // Check update permission
            if (!hasPermission(user_permissions, 'documents', 'update')) {
                throw new Error('Insufficient permissions: Cannot update documents');
            }

            const response = await axios2.put(`/datahub/database/update/${documentId}`, {
                projectId,
                collection,
                updates,
                user_token,
                permissions: user_permissions,
                groups: user_groups,
                audit: {
                    action: 'update_document',
                    resource_type: 'documents',
                    details: {
                        collection: collection,
                        document_id: documentId,
                        update_keys: Object.keys(updates)
                    }
                }
            });

            // Create audit entry
            const auditEntry = createAuditEntry(user_id, 'update_document', 'documents', documentId, true);

            return response.data;
        } catch (error) {
            // Create audit entry for failed operation
            const { user_id } = getUserContext();
            const auditEntry = createAuditEntry(user_id, 'update_document', 'documents', documentId, false, error.message);

            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const deleteDocument = createAsyncThunk(
    'datahubDatabase/deleteDocument',
    async ({ projectId, collection, documentId }, { rejectWithValue }) => {
        try {
            const { user_token, user_permissions, user_groups, user_id } = getUserContext();

            // Check delete permission
            if (!hasPermission(user_permissions, 'documents', 'delete')) {
                throw new Error('Insufficient permissions: Cannot delete documents');
            }

            await axios2.delete(`/datahub/database/delete/${documentId}`, {
                params: {
                    projectId,
                    collection,
                    user_token,
                    permissions: user_permissions,
                    groups: user_groups,
                    audit: {
                        action: 'delete_document',
                        resource_type: 'documents',
                        details: {
                            collection: collection,
                            document_id: documentId
                        }
                    }
                }
            });

            // Create audit entry
            const auditEntry = createAuditEntry(user_id, 'delete_document', 'documents', documentId, true);

            return { documentId, collection };
        } catch (error) {
            // Create audit entry for failed operation
            const { user_id } = getUserContext();
            const auditEntry = createAuditEntry(user_id, 'delete_document', 'documents', documentId, false, error.message);

            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

export const exportDocuments = createAsyncThunk(
    'datahubDatabase/exportDocuments',
    async ({ projectId, collection, query = {} }, { rejectWithValue }) => {
        try {
            const { user_token, user_permissions, user_groups, user_id } = getUserContext();

            // Check export permission
            if (!hasPermission(user_permissions, 'documents', 'export')) {
                throw new Error('Insufficient permissions: Cannot export documents');
            }

            const response = await axios2.post(
                '/datahub/database/export',
                {
                    projectId,
                    collection,
                    query,
                    user_token,
                    permissions: user_permissions,
                    groups: user_groups,
                    audit: {
                        action: 'export_documents',
                        resource_type: 'documents',
                        details: {
                            collection: collection,
                            query: query
                        }
                    }
                },
                {
                    responseType: 'blob'
                }
            );

            // Create audit entry
            const auditEntry = createAuditEntry(user_id, 'export_documents', 'documents', collection, true);

            return { collection, blob: response.data };
        } catch (error) {
            // Create audit entry for failed operation
            const { user_id } = getUserContext();
            const auditEntry = createAuditEntry(user_id, 'export_documents', 'documents', collection, false, error.message);

            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

// ----------------------------------------------------------------------

const initialState = {
    overview: null,
    collections: [],
    documents: [],
    currentCollection: null,
    loading: false,
    error: null,
    stats: {
        totalCollections: 0,
        totalDocuments: 0,
        totalSize: 0,
        activeConnections: 0
    }
};

// ----------------------------------------------------------------------

const slice = createSlice({
    name: 'datahubDatabase',
    initialState,
    reducers: {
        setCurrentCollection(state, action) {
            state.currentCollection = action.payload;
        },
        clearError(state) {
            state.error = null;
        },
        clearDocuments(state) {
            state.documents = [];
        }
    },
    extraReducers: (builder) => {
        builder
            // fetchDatabaseOverview
            .addCase(fetchDatabaseOverview.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDatabaseOverview.fulfilled, (state, action) => {
                state.loading = false;
                state.overview = action.payload;
                state.collections = action.payload.collections || [];
                state.stats = action.payload.stats || state.stats;
            })
            .addCase(fetchDatabaseOverview.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // queryDocuments
            .addCase(queryDocuments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(queryDocuments.fulfilled, (state, action) => {
                state.loading = false;
                state.documents = action.payload.documents || [];
                state.currentCollection = action.payload.collection || state.currentCollection;
            })
            .addCase(queryDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // insertDocument
            .addCase(insertDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(insertDocument.fulfilled, (state, action) => {
                state.loading = false;
                state.documents.push(action.payload);
            })
            .addCase(insertDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // updateDocument
            .addCase(updateDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateDocument.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.documents.findIndex((doc) => doc._id === action.payload._id);
                if (index !== -1) {
                    state.documents[index] = action.payload;
                }
            })
            .addCase(updateDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // deleteDocument
            .addCase(deleteDocument.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteDocument.fulfilled, (state, action) => {
                state.loading = false;
                state.documents = state.documents.filter((doc) => doc._id !== action.payload.documentId);
            })
            .addCase(deleteDocument.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // exportDocuments
            .addCase(exportDocuments.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(exportDocuments.fulfilled, (state, action) => {
                state.loading = false;
                // Handle file download
                const url = window.URL.createObjectURL(action.payload.blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${action.payload.collection}_export.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .addCase(exportDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

// ----------------------------------------------------------------------

// Reducer
export default slice.reducer;

// Actions
export const { setCurrentCollection, clearError, clearDocuments } = slice.actions;
